// Warehouse & Location Hierarchy Configuration Types

export interface HierarchyLevel {
  level: number;
  displayName: string;
  codePrefix: string;
  isActive: boolean;
}

export interface WarehouseHierarchyConfig {
  numberOfLevels: number;
  levels: HierarchyLevel[];
  capacityTrackingEnabled: boolean;
  lastModified?: Date;
  modifiedBy?: string;
}

export const DEFAULT_HIERARCHY_CONFIG: WarehouseHierarchyConfig = {
  numberOfLevels: 5,
  levels: [
    {
      level: 1,
      displayName: "Warehouse",
      codePrefix: "WH",
      isActive: true,
    },
    {
      level: 2,
      displayName: "Aisle",
      codePrefix: "A",
      isActive: true,
    },
    {
      level: 3,
      displayName: "Rack",
      codePrefix: "R",
      isActive: true,
    },
    {
      level: 4,
      displayName: "Shelf",
      codePrefix: "S",
      isActive: true,
    },
    {
      level: 5,
      displayName: "Bin",
      codePrefix: "B",
      isActive: true,
    },
  ],
  capacityTrackingEnabled: true,
};

export const LEVEL_PLACEHOLDER_EXAMPLES = [
  "Warehouse, Building, Site, Facility",
  "Aisle, Zone, Room, Area, Floor",
  "Rack, Bay, Section",
  "Shelf, Level",
  "Bin, Position, Slot",
];

export interface ValidationError {
  field: string;
  message: string;
}

export function validateHierarchyConfig(
  config: WarehouseHierarchyConfig
): ValidationError[] {
  const errors: ValidationError[] = [];

  // Validate active levels have display names
  const activeLevels = config.levels.filter((l) => l.isActive);
  activeLevels.forEach((level) => {
    if (!level.displayName.trim()) {
      errors.push({
        field: `level${level.level}DisplayName`,
        message: `Display name is required for Level ${level.level}`,
      });
    }
  });

  // Check for duplicate display names
  const displayNames = activeLevels.map((l) => l.displayName.toLowerCase());
  const duplicateNames = displayNames.filter(
    (name, index) => displayNames.indexOf(name) !== index
  );
  if (duplicateNames.length > 0) {
    errors.push({
      field: "displayNames",
      message: "Display names must be unique across all levels",
    });
  }

  // Check for duplicate code prefixes (if provided)
  const codePrefixes = activeLevels
    .map((l) => l.codePrefix.trim().toLowerCase())
    .filter((p) => p.length > 0);
  const duplicatePrefixes = codePrefixes.filter(
    (prefix, index) => codePrefixes.indexOf(prefix) !== index
  );
  if (duplicatePrefixes.length > 0) {
    errors.push({
      field: "codePrefixes",
      message: "Code prefixes must be unique across all levels",
    });
  }

  return errors;
}

export function generateLocationCodePreview(
  config: WarehouseHierarchyConfig
): string {
  const activeLevels = config.levels
    .filter((l) => l.isActive)
    .sort((a, b) => a.level - b.level);

  if (activeLevels.length === 0) {
    return "No levels configured";
  }

  const parts = activeLevels.map((level) => {
    const prefix = level.codePrefix.trim();
    if (prefix) {
      return `${prefix}01`;
    }
    return `${level.displayName.substring(0, 3).toUpperCase()}01`;
  });

  return parts.join("-");
}
